import { apiService } from './api.service';
import type {
  Risk,
  CreateRiskDto,
  UpdateRiskDto,
  Mitigation,
  CreateMitigationDto,
  UpdateMitigationDto,
  Suggestion,
} from 'shared';

// =============================================================================
// RISK API
// =============================================================================

export interface RiskFilters {
  spaceId?: string;
  componentId?: string;
  status?: string;
  riskTreatment?: string;
}

/**
 * Build query string from filters
 */
function buildQueryString(params: Record<string, string | undefined>): string {
  const searchParams = new globalThis.URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
}

export const riskApi = {
  /**
   * Get all risks, optionally filtered
   */
  async getAll(filters?: RiskFilters): Promise<Risk[]> {
    const query = buildQueryString({
      spaceId: filters?.spaceId,
      componentId: filters?.componentId,
      status: filters?.status,
      riskTreatment: filters?.riskTreatment,
    });
    const url = query ? `/risks?${query}` : '/risks';
    return apiService.get<Risk[]>(url);
  },

  /**
   * Get risks for a specific component
   */
  async getByComponent(componentId: string): Promise<Risk[]> {
    return apiService.get<Risk[]>(`/risks/component/${componentId}`);
  },

  /**
   * Get risks for a specific space
   */
  async getBySpace(spaceId: string): Promise<Risk[]> {
    return apiService.get<Risk[]>(`/risks/space/${spaceId}`);
  },

  /**
   * Get a single risk by ID
   */
  async getById(id: string): Promise<Risk> {
    return apiService.get<Risk>(`/risks/${id}`);
  },

  /**
   * Create a new risk
   */
  async create(data: CreateRiskDto): Promise<Risk> {
    return apiService.post<Risk>('/risks', data);
  },

  /**
   * Update an existing risk
   */
  async update(id: string, data: UpdateRiskDto): Promise<Risk> {
    return apiService.put<Risk>(`/risks/${id}`, data);
  },

  /**
   * Delete a risk
   */
  async delete(id: string): Promise<void> {
    return apiService.delete(`/risks/${id}`);
  },

  /**
   * Calculate inherent risk score
   * Formula: (avgCIA × likelihood) / 10
   */
  calculateInherentRisk(
    confidentiality: number,
    integrity: number,
    availability: number,
    likelihood: number
  ): number {
    // Ensure all values are valid numbers, default to 5 if not
    const c = Number(confidentiality) || 5;
    const i = Number(integrity) || 5;
    const a = Number(availability) || 5;
    const l = Number(likelihood) || 5;

    const avgImpact = (c + i + a) / 3;
    return parseFloat(((avgImpact * l) / 10).toFixed(2));
  },

  /**
   * Get risk level string from score
   */
  getRiskLevel(
    score: number
  ): 'critical' | 'high' | 'medium' | 'low' | 'minimal' {
    if (score >= 8.0) return 'critical';
    if (score >= 6.0) return 'high';
    if (score >= 4.0) return 'medium';
    if (score >= 2.0) return 'low';
    return 'minimal';
  },

  /**
   * Get risk level color (Avinor brand)
   */
  getRiskColor(score: number): string {
    const level = this.getRiskLevel(score);
    const colors = {
      critical: '#FE3B00', // Avinor orange
      high: '#2D3B45', // Omni-Tek steel
      medium: '#FFBB00', // Avinor okergul
      low: '#42B8B2', // Avinor mintblå
      minimal: '#939597', // Avinor grå 04
    };
    return colors[level];
  },
};

// =============================================================================
// MITIGATION API
// =============================================================================

export interface MitigationFilters {
  riskId?: string;
  status?: string;
  controlType?: string;
}

export const mitigationApi = {
  /**
   * Get all mitigations, optionally filtered
   */
  async getAll(filters?: MitigationFilters): Promise<Mitigation[]> {
    const query = buildQueryString({
      riskId: filters?.riskId,
      status: filters?.status,
      controlType: filters?.controlType,
    });
    const url = query ? `/mitigations?${query}` : '/mitigations';
    return apiService.get<Mitigation[]>(url);
  },

  /**
   * Get mitigations for a specific risk
   */
  async getByRisk(riskId: string): Promise<Mitigation[]> {
    return apiService.get<Mitigation[]>(`/mitigations/risk/${riskId}`);
  },

  /**
   * Get a single mitigation by ID
   */
  async getById(id: string): Promise<Mitigation> {
    return apiService.get<Mitigation>(`/mitigations/${id}`);
  },

  /**
   * Create a new mitigation
   */
  async create(data: CreateMitigationDto): Promise<Mitigation> {
    return apiService.post<Mitigation>('/mitigations', data);
  },

  /**
   * Update an existing mitigation
   */
  async updateMitigation(
    id: string,
    data: UpdateMitigationDto
  ): Promise<Mitigation> {
    return apiService.put<Mitigation>(`/mitigations/${id}`, data);
  },

  /**
   * Delete a mitigation
   */
  async delete(id: string): Promise<void> {
    return apiService.delete(`/mitigations/${id}`);
  },
};

// =============================================================================
// SUGGESTION API
// =============================================================================

export interface SuggestionContext {
  componentId: string;
  spaceId: string;
  diagramId?: string;
  hopDepth?: number;
}

export const suggestionApi = {
  /**
   * Generate suggestions for a component
   */
  async getForComponent(context: SuggestionContext): Promise<Suggestion[]> {
    const query = buildQueryString({
      spaceId: context.spaceId,
      diagramId: context.diagramId,
      hopDepth: context.hopDepth?.toString(),
    });

    return apiService.get<Suggestion[]>(
      `/suggestions/component/${context.componentId}?${query}`
    );
  },
};

// =============================================================================
// CATALOG API
// =============================================================================

export interface AttackTactic {
  id: string;
  name: string;
  description: string;
}

export interface AttackTechnique {
  id: string;
  name: string;
  description: string;
  url: string;
  tactics: string[]; // Tactic shortnames (e.g., 'initial-access')
  tacticIds?: string[]; // Legacy field for backwards compatibility
  platforms?: string[];
  isSubtechnique?: boolean;
  parentId?: string;
}

export interface D3fendTactic {
  id: string;
  name: string;
  description: string;
}

export interface D3fendTechnique {
  id: string;
  name: string;
  description: string;
  tacticId: string;
  countersAttack?: string[];
  url?: string;
}

export interface IsoControlCategory {
  id: string;
  name: string;
  nameNb: string;
}

export interface IsoControl {
  id: string;
  name: string;
  nameNb: string;
  categoryId: string;
  appliesTo: ('confidentiality' | 'integrity' | 'availability')[];
}

export const catalogApi = {
  // MITRE ATT&CK
  attack: {
    async getTactics(): Promise<AttackTactic[]> {
      return apiService.get<AttackTactic[]>('/catalogs/attack/tactics');
    },

    async getTechniques(tacticId?: string): Promise<AttackTechnique[]> {
      const url = tacticId
        ? `/catalogs/attack/techniques?tacticId=${tacticId}`
        : '/catalogs/attack/techniques';
      return apiService.get<AttackTechnique[]>(url);
    },

    async getTechniqueById(id: string): Promise<AttackTechnique | null> {
      return apiService.get<AttackTechnique | null>(
        `/catalogs/attack/techniques/${id}`
      );
    },

    async search(query: string): Promise<AttackTechnique[]> {
      return apiService.get<AttackTechnique[]>(
        `/catalogs/attack/search?q=${encodeURIComponent(query)}`
      );
    },
  },

  // MITRE D3FEND
  d3fend: {
    async getTactics(): Promise<D3fendTactic[]> {
      return apiService.get<D3fendTactic[]>('/catalogs/d3fend/tactics');
    },

    async getTechniques(tacticId?: string): Promise<D3fendTechnique[]> {
      const url = tacticId
        ? `/catalogs/d3fend/techniques?tacticId=${tacticId}`
        : '/catalogs/d3fend/techniques';
      return apiService.get<D3fendTechnique[]>(url);
    },

    async getTechniqueById(id: string): Promise<D3fendTechnique | null> {
      return apiService.get<D3fendTechnique | null>(
        `/catalogs/d3fend/techniques/${id}`
      );
    },

    async getCountersForAttack(attackId: string): Promise<D3fendTechnique[]> {
      return apiService.get<D3fendTechnique[]>(
        `/catalogs/d3fend/counters/${attackId}`
      );
    },

    async search(query: string): Promise<D3fendTechnique[]> {
      return apiService.get<D3fendTechnique[]>(
        `/catalogs/d3fend/search?q=${encodeURIComponent(query)}`
      );
    },
  },

  // ISO 27001:2022
  iso: {
    async getCategories(): Promise<IsoControlCategory[]> {
      return apiService.get<IsoControlCategory[]>(
        '/catalogs/iso27001/categories'
      );
    },

    async getControls(
      categoryId?: string,
      cia?: 'confidentiality' | 'integrity' | 'availability'
    ): Promise<IsoControl[]> {
      const query = buildQueryString({
        categoryId,
        cia,
      });
      const url = query
        ? `/catalogs/iso27001/controls?${query}`
        : '/catalogs/iso27001/controls';
      return apiService.get<IsoControl[]>(url);
    },

    async getControlById(id: string): Promise<IsoControl | null> {
      return apiService.get<IsoControl | null>(
        `/catalogs/iso27001/controls/${id}`
      );
    },

    async search(query: string): Promise<IsoControl[]> {
      return apiService.get<IsoControl[]>(
        `/catalogs/iso27001/search?q=${encodeURIComponent(query)}`
      );
    },
  },

  // Component Risk Profiles
  riskProfiles: {
    async getAll(): Promise<unknown[]> {
      return apiService.get<unknown[]>('/catalogs/risk-profiles');
    },

    async getByType(componentType: string): Promise<unknown | null> {
      return apiService.get<unknown | null>(
        `/catalogs/risk-profiles/${componentType}`
      );
    },
  },
};

// =============================================================================
// AI API
// =============================================================================

export interface AiAnalysisContext {
  componentId: string;
  spaceId: string;
  mode: 'analyze' | 'attack';
  includeRelations?: boolean;
  maxDepth?: number;
}

export interface AttackScenario {
  id: string;
  name: string;
  description: string;
  attackPath: Array<{
    step: number;
    technique: string;
    techniqueId?: string;
    description: string;
    targetComponent?: string;
  }>;
  impact: 'critical' | 'high' | 'medium' | 'low';
  likelihood: number;
  mitigations: string[];
}

export interface AiAnalysisResult {
  suggestions: Suggestion[];
  analysis: string;
  attackScenarios?: AttackScenario[];
  mode: 'analyze' | 'attack';
  modelUsed: string;
  tokensUsed: number;
}

export interface AiStatusResponse {
  enabled: boolean;
  model: string;
  message: string;
}

export const aiApi = {
  /**
   * Check if AI service is available
   */
  async getStatus(): Promise<AiStatusResponse> {
    return apiService.get<AiStatusResponse>('/ai/status');
  },

  /**
   * Analyze a component using AI
   */
  async analyzeComponent(
    context: AiAnalysisContext
  ): Promise<AiAnalysisResult> {
    return apiService.post<AiAnalysisResult>('/ai/analyze', context);
  },

  /**
   * Perform security analysis (shorthand)
   */
  async securityAnalysis(
    componentId: string,
    spaceId: string,
    includeRelations?: boolean
  ): Promise<AiAnalysisResult> {
    return apiService.post<AiAnalysisResult>('/ai/analyze/security', {
      componentId,
      spaceId,
      includeRelations,
    });
  },

  /**
   * Perform attack simulation (shorthand)
   */
  async attackSimulation(
    componentId: string,
    spaceId: string,
    includeRelations?: boolean,
    maxDepth?: number
  ): Promise<AiAnalysisResult> {
    return apiService.post<AiAnalysisResult>('/ai/analyze/attack', {
      componentId,
      spaceId,
      includeRelations,
      maxDepth,
    });
  },

  /**
   * Analyze a specific risk and suggest mitigations
   */
  async riskAnalysis(
    riskId: string,
    spaceId: string
  ): Promise<AiAnalysisResult> {
    return apiService.post<AiAnalysisResult>('/ai/analyze/risk', {
      riskId,
      spaceId,
    });
  },
};
